import { toast } from 'react-hot-toast';
import React, { ReactElement } from 'react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationOptions {
  duration?: number;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';
}

const defaultOptions: NotificationOptions = {
  duration: 5000,
  position: 'top-right'
};

export const showNotification = (
  message: string | ReactElement,
  type: NotificationType = 'info',
  options: NotificationOptions = {}
): void => {
  const { duration, position } = { ...defaultOptions, ...options };

  switch (type) {
    case 'success':
      toast.success(message, { duration, position });
      break;
    case 'error':
      toast.error(message, { duration, position });
      break;
    case 'info':
      toast(message, { duration, position });
      break;
    case 'warning':
      toast(message, {
        duration,
        position,
        icon: '⚠️',
        style: {
          background: '#fff7ed',
          color: '#9a3412',
          border: '1px solid #fed7aa'
        }
      });
      break;
  }
};

// Helper functions for common notifications
export const showSuccess = (message: string | ReactElement, options?: NotificationOptions): void => 
  showNotification(message, 'success', options);

export const showError = (message: string | ReactElement, options?: NotificationOptions): void => 
  showNotification(message, 'error', options);

export const showInfo = (message: string | ReactElement, options?: NotificationOptions): void => 
  showNotification(message, 'info', options);

export const showWarning = (message: string | ReactElement, options?: NotificationOptions): void => 
  showNotification(message, 'warning', options);

// Special notifications
export const showAPIError = (error: any): void => {
  const message = error.response?.data?.message || error.message || 'An unexpected error occurred';
  const code = error.response?.data?.code;
  
  if (code === 'JOB_LIMIT_EXCEEDED') {
    toast.error(
      React.createElement('div', {},
        React.createElement('p', {}, message),
        React.createElement('button', {
          onClick: () => window.location.href = '/upgrade',
          className: 'mt-2 text-sm font-medium text-blue-600 hover:text-blue-500'
        }, 'Upgrade Now →')
      ),
      { duration: 6000 }
    );
  } else {
    showError(message);
  }
};

export const showNetworkError = (): void => 
  showError('Network error. Please check your connection and try again.');

export const showRateLimitError = (): void => 
  showWarning('Too many requests. Please wait a moment before trying again.');

export const showAuthError = (): void => 
  showError('Authentication error. Please log in again.'); 